//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';

import LoadingShimmer from '@components/atoms/loading-shimmer/LoadingShimmer';
import StatText from '@components/atoms/stat-bubble/StatText';
import {
  LoadingShimmerWrapper,
  StatContainer,
  StyledBodyLabelText,
  StyledStatBubbleSheet,
} from '@components/molecules/stat-bubble/StatBubbleStyles';
import { StatComparisonChip } from '@components/molecules/stat-bubble/StatComparisonChip';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent } from '@utils/withTracking';

export interface IStatBubbleComparisonStat {
  percentage: string;
  tooltipText?: string | undefined;
  trendingIcon: UnisonIconType;
}

interface IOwnProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  alertText?: string | React.ReactElement;
  alertColor?: React.ComponentProps<typeof StatText>['alertColor'];
  comparisonStat?: IStatBubbleComparisonStat;
  customStatIcon?: React.ReactElement;
  disclaimerText?: string;
  helpText?: string;
  isExportReport?: boolean;
  isLoading?: boolean;
  title: string;
  value: string | number;
}

export type Props = IOwnProps;

/**
 * The StatBubble molecule is a sheet with our standard stat bubble
 * styling, with the optional ability to add a child atom to display
 * below the stat.
 */
export const StatBubble = ({
  alertColor,
  alertText,
  children,
  className,
  comparisonStat,
  customStatIcon,
  disclaimerText,
  helpText,
  isExportReport = false,
  isLoading = false,
  title,
  trackingId,
  value,
}: Props) => {
  const valueTrackingId = trackingId ? `${trackingId}-value` : undefined;
  const comparisonTrackingId = trackingId
    ? `${trackingId}-comparison`
    : undefined;
  const helpIconTrackingId = trackingId ? `${trackingId}-help-icon` : undefined;

  return (
    <StyledStatBubbleSheet
      $isExportReport={isExportReport}
      className={className}
      trackingId={trackingId}
    >
      <StyledBodyLabelText
        helpText={isExportReport ? undefined : helpText}
        placement='right'
        title={title}
        trackingId={helpIconTrackingId}
      />
      <StatContainer>
        {isLoading ? (
          <LoadingShimmerWrapper>
            <LoadingShimmer height='22px' width='100%' />
          </LoadingShimmerWrapper>
        ) : (
          <>
            {customStatIcon}
            <StatText
              alertColor={alertColor}
              alertText={alertText}
              disclaimerText={disclaimerText}
              mixin={Mixins.Text.Subtitle}
              trackingId={valueTrackingId}
              value={value}
            />
            <StatComparisonChip
              comparisonStat={comparisonStat}
              trackingId={comparisonTrackingId}
            />
          </>
        )}
      </StatContainer>
      {children}
    </StyledStatBubbleSheet>
  );
};

export default StatBubble;
